import { useEffect, useRef } from "react";
import { useSelector } from 'react-redux';
import octopusInstance from "../../services/octopus/octopusInstance";
import { store } from "../../context/NotificationContext";

function useEquityMarginAlerts() {
  const auth = useSelector((state) => state.auth);
  const handlerRef = useRef(null);
  const setupAttempts = useRef(0);

  const setupHandler = () => {
    try {
      const clientId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");

      if (!clientId || !authToken) {
        return;
      }

      // Force close any existing connection
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }

      handlerRef.current = octopusInstance.wsHandler({
        messageType: "EquityMarginAlert",
        subscriptionLocation: "app-equity-margin",
        payload: {}
      });

      if (handlerRef.current) {
        handlerRef.current.subscribe((data) => {
          const { msg } = data || {};
          if (msg && typeof msg === 'object') {
            console.log("Equity margin alert received from socket:", msg);
            
            // Show popup notification using global store if available
            if (store.showNotification) {
              store.showNotification('EQUITY_MARGIN_ALERT', msg);
            }
          }
        });
      }
    } catch (error) {
      console.error('Error setting up equity margin alert socket handler:', error);
    }
  };

  // Attempt connection when auth state changes
  useEffect(() => {
    if (auth?.isLoggedIn) {
      setupHandler();
    }
  }, [auth?.isLoggedIn]);

  // Retry connection on mount and periodically if needed
  useEffect(() => {
    const attemptConnection = () => {
      const authToken = localStorage.getItem("auth_token");
      const clientId = localStorage.getItem("login_id");
      
      if (authToken && clientId && !handlerRef.current && setupAttempts.current < 3) {
        setupAttempts.current += 1;
        setupHandler();
      }
    };

    attemptConnection();

    const retryTimer = setInterval(() => {
      if (!handlerRef.current && setupAttempts.current < 3) {
        attemptConnection();
      }
    }, 2000);

    return () => {
      clearInterval(retryTimer);
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }
    };
  }, []);

  // Reconnect on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setupAttempts.current = 0; // Reset attempts counter
        setupHandler();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
}

export default useEquityMarginAlerts;
